/**
 * Document Verification Utility
 * Verifies Ed25519 cryptographic signatures on QR codes offline.
 * Uses tweetnacl for Ed25519 verification (zero dependencies, works offline).
 */

import nacl from 'tweetnacl';
import { decodeBase64, encodeUTF8 } from 'tweetnacl-util';

// QR payload constants
const QR_PREFIX = 'BNFT';
const QR_VERSION = '1';

// LocalStorage key for the public key
const PUBLIC_KEY_STORAGE_KEY = 'bnft-ed25519-public-key';

/** Document types found in signed QR codes */
export type SignedDocumentType = 'LVO' | 'TAE' | 'INV';

/** Parsed data from a signed LVO QR code */
export interface LvoDocumentData {
  t: 'LVO';
  r: string;   // reference
  d: string;   // date
  s: string;   // structure
  dep: string;  // departure city
  arr: string;  // arrival city
  depC: string; // departure country
  arrC: string; // arrival country
  exp: string;  // sender (expéditeur)
  dest: string; // recipient (destinataire)
  tr: string;   // transporter
  v: string;   // tractor registration
  v2: string;  // trailer registration
  dr: string;  // driver name
  drL: string; // driver license number
  gds: string; // goods summary
  itn: string; // itinerary
  px: string;  // transport price
  fee: string; // fee amount (redevance)
  q: string;   // quota ("1" = quota, "0" = national)
}

/** Parsed data from a signed TAE QR code */
export interface TaeDocumentData {
  t: 'TAE';
  r: string;   // reference
  d: string;   // date
  veh: string;  // vehicle registration
  vin: string;  // VIN / chassis number
  vC: string;  // vehicle country
  own: string;  // owner name
  ag: string;  // agency name
  vf: string;  // valid_from
  vu: string;  // valid_until
  mt: string;  // amount
  val: string; // validity days
}

/** Parsed data from a signed Invoice QR code */
export interface InvoiceDocumentData {
  t: 'INV';
  r: string;   // reference
  d: string;   // date
  ti: string;  // title
  tp: string;  // invoice type (LV, TAE)
  mt: string;  // total amount
  pd: string;  // paid amount
  cur: string; // currency code
  cl: string;  // client name
  bn: string;  // beneficiary name
  st: string;  // status
  dpay: string; // payment date
}

export type DocumentData = LvoDocumentData | TaeDocumentData | InvoiceDocumentData;

/** Result of QR code verification */
export interface VerificationResult {
  valid: boolean;
  documentType?: SignedDocumentType;
  reference?: string;
  data?: DocumentData;
  error?: string;
}

/**
 * Base64url decode (handles missing padding).
 */
function base64urlDecode(s: string): Uint8Array {
  // Add padding
  let padded = s;
  const padding = 4 - (padded.length % 4);
  if (padding !== 4) {
    padded += '='.repeat(padding);
  }
  // Convert base64url to standard base64
  const b64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  return decodeBase64(b64);
}

/**
 * Check if a QR code contains the signed BNFT format.
 */
export function isSignedQRCode(qrData: string): boolean {
  return qrData.startsWith(`${QR_PREFIX}:${QR_VERSION}:`);
}

/**
 * Parse a signed QR payload into data and signature bytes.
 * Returns null if the format is invalid.
 */
export function parseQRPayload(
  qrData: string,
): { dataBytes: Uint8Array; signature: Uint8Array; data: DocumentData } | null {
  if (!isSignedQRCode(qrData)) return null;

  try {
    const content = qrData.slice(`${QR_PREFIX}:${QR_VERSION}:`.length);
    const dotIndex = content.lastIndexOf('.');
    if (dotIndex === -1) return null;

    const dataB64 = content.slice(0, dotIndex);
    const sigB64 = content.slice(dotIndex + 1);

    const dataBytes = base64urlDecode(dataB64);
    const signature = base64urlDecode(sigB64);

    if (signature.length !== 64) return null;

    const jsonStr = encodeUTF8(dataBytes);
    const data = JSON.parse(jsonStr) as DocumentData;

    return { dataBytes, signature, data };
  } catch {
    return null;
  }
}

/**
 * Extract the document reference from a signed QR payload.
 * Works without verifying the signature (for quick extraction).
 */
export function extractReferenceFromSignedQR(qrData: string): string | null {
  const parsed = parseQRPayload(qrData);
  if (!parsed) return null;
  return parsed.data.r || null;
}

/**
 * Get the stored public key from localStorage.
 */
function getStoredPublicKey(): Uint8Array | null {
  try {
    const stored = localStorage.getItem(PUBLIC_KEY_STORAGE_KEY);
    if (!stored) return null;
    return decodeBase64(stored);
  } catch {
    return null;
  }
}

/**
 * Store the public key in localStorage for offline access.
 */
export function storePublicKey(publicKeyBase64: string): void {
  localStorage.setItem(PUBLIC_KEY_STORAGE_KEY, publicKeyBase64);
}

/**
 * Verify the Ed25519 signature of a signed QR code payload.
 * Uses the public key stored in localStorage.
 * Works fully offline.
 */
export function verifySignature(qrData: string): VerificationResult {
  const publicKey = getStoredPublicKey();
  if (!publicKey) {
    return {
      valid: false,
      error: 'PUBLIC_KEY_NOT_AVAILABLE',
    };
  }

  const parsed = parseQRPayload(qrData);
  if (!parsed) {
    return {
      valid: false,
      error: 'INVALID_FORMAT',
    };
  }

  try {
    const isValid = nacl.sign.detached.verify(
      parsed.dataBytes,
      parsed.signature,
      publicKey,
    );

    if (!isValid) {
      return {
        valid: false,
        error: 'INVALID_SIGNATURE',
      };
    }

    return {
      valid: true,
      documentType: parsed.data.t as SignedDocumentType,
      reference: parsed.data.r,
      data: parsed.data,
    };
  } catch {
    return {
      valid: false,
      error: 'VERIFICATION_ERROR',
    };
  }
}
